/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Text, useToast } from '@chakra-ui/react';
import { TOAST_SETTINGS } from '../../../constants';
import { getClientStates } from '../../../redux/action/client.action';
import Dropdown from '../../Dropdown';

const TaxRegionDropdown = function ({
  selectedState,
  setSelectedState,
  clientId,
  showAll = true,
  disabled = false,
  validationObj,
  refresh,
  label = 'Tax Region',
}) {
  const toast = useToast();
  const [states, setStates] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isValid, setValid] = useState(validationObj?.isvalid || true);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Fetch all states with pagination handling
   * Fetches all pages and combines results
   */
  const fetchAllStates = async () => {
    if (!clientId) {
      setStates([]);
      return;
    }

    setIsLoading(true);
    try {
      // Fetch first page to get total pages
      const firstPageResult = await getClientStates(clientId, 50, 1);

      if (!firstPageResult.ok || !firstPageResult.data) {
        setStates([]);
        setIsLoading(false);
        return;
      }

      const totalPages = firstPageResult.data.pagination?.total_pages || 1;
      const firstPageStates = firstPageResult.data.tax_regions.map((state) => ({
        label: state.name,
        value: state.id,
      }));

      // If only one page, use first page data
      if (totalPages === 1) {
        const allStates = showAll
          ? [{ label: 'All', value: null }, ...firstPageStates]
          : firstPageStates;
        setStates(allStates);
        setIsLoading(false);
        return;
      }

      // Fetch remaining pages in parallel
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const remainingResults = await Promise.all(
        remainingPages.map((page) => getClientStates(clientId, 50, page))
      );

      // Combine all results
      let allStatesData = [...firstPageStates];
      remainingResults.forEach((result) => {
        if (result.ok && result.data) {
          const fetchedStates = result.data.tax_regions.map((state) => ({
            label: state.name,
            value: state.id,
          }));
          allStatesData = [...allStatesData, ...fetchedStates];
        }
      });

      // Add "All" option at the beginning if showAll is true
      const finalStates = showAll
        ? [{ label: 'All', value: null }, ...allStatesData]
        : allStatesData;
      setStates(finalStates);
    } catch (error) {
      console.error('Error fetching states:', error);
      toast({
        ...TOAST_SETTINGS.SETTINGS,
        status: TOAST_SETTINGS.ERROR,
        title: 'Failed to fetch tax regions',
      });
      setStates([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllStates();
  }, [clientId, showAll]);

  useEffect(() => {
    if (validationObj && !validationObj.isvalid) {
      setErrorMsg(validationObj.errorMessage);
      setValid(validationObj.isvalid);
    }
  }, [refresh, validationObj]);

  const setStateData = (e) => {
    setErrorMsg(null);
    setValid(true);
    setSelectedState(e);
  };

  return (
    <>
      <Dropdown
        label={`${label}${validationObj?.required ? '*' : ''}`}
        onSelect={setStateData}
        options={states}
        value={selectedState}
        placeholder="Select"
        isError={!isValid}
        disabled={disabled || isLoading}
      />
      <Text visibility={isValid ? 'hidden' : 'visible'} color="main.error" mb="2px" fontSize="11px">
        {errorMsg || 'Error !'}
      </Text>
    </>
  );
};

export default TaxRegionDropdown;
